import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useLocation} from "react-router-dom";
import api from "../../api";
import {Button, Col, DatePicker, Form, Input, message, Modal, Row, Table, Tabs, Select, Checkbox} from "antd";
import {EditOutlined, MailOutlined, PlusOutlined, DeleteOutlined} from "@ant-design/icons";
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import {asArray, buildStaticChartConfig} from './adminStats';

const { TextArea } = Input;

const StaticChart = ({options, ...props}) => (
    <Chart options={buildStaticChartConfig(options)} {...props} />
);

export default function AdminIndex() {
    const navigate = useNavigate();
    const location = useLocation();
    const [users, setUsers] = useState([]);
    const [blogPosts, setBlogPosts] = useState([]);
    const [generateLoading, setGenerateLoading] = useState(false);
    const [generateModalOpen, setGenerateModalOpen] = useState(false);
    const [companyGrowthData, setCompanyGrowthData] = useState([]);
    const [revenueGrowthData, setRevenueGrowthData] = useState([]);
    const [signupsPerDayData, setSignupsPerDayData] = useState([]);
    const [utmStats, setUtmStats] = useState([]);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);
    const [dateRangePreset, setDateRangePreset] = useState('last_30_days');
    const [generateForm] = Form.useForm();
    const [searchForm] = Form.useForm();
    const [editPlanForm] = Form.useForm();
    const [plans, setPlans] = useState([]);
    const [editPlanModalOpen, setEditPlanModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editPlanLoading, setEditPlanLoading] = useState(false);
    const [onlyNewCustomers, setOnlyNewCustomers] = useState(false);
    const [userCountByLanguage, setUserCountByLanguage] = useState([]);
    const [annotations, setAnnotations] = useState([]);
    const [annotationModalOpen, setAnnotationModalOpen] = useState(false);
    const [annotationForm] = Form.useForm();
    const [annotationLoading, setAnnotationLoading] = useState(false);
    const [editAnnotationModalOpen, setEditAnnotationModalOpen] = useState(false);
    const [editAnnotationForm] = Form.useForm();
    const [editAnnotationLoading, setEditAnnotationLoading] = useState(false);
    const [editingAnnotation, setEditingAnnotation] = useState(null);

    const { RangePicker } = DatePicker;

    // Determine active tab based on route
    const getActiveTab = () => {
        if (location.pathname === '/admin/suppliers') return 'suppliers';
        if (location.pathname === '/admin/icons') return 'icons';
        if (location.pathname === '/admin/users') return 'users';
        if (location.pathname === '/admin/blog') return 'blog';
        if (location.pathname === '/admin/ad-spends') return 'ad-spends';
        if (location.pathname === '/admin/help-videos') return 'help-videos';
        if (location.pathname === '/admin/affiliates') return 'affiliates';
        return 'stats';
    };

    const handlePresetChange = (preset) => {
        setDateRangePreset(preset);
        let newRange;

        switch(preset) {
            case 'all_time':
                newRange = [dayjs('2020-01-01'), dayjs()];
                break;
            case 'last_year':
                newRange = [dayjs().subtract(1, 'year'), dayjs()];
                break;
            case 'ytd':
                newRange = [dayjs().startOf('year'), dayjs()];
                break;
            case 'last_30_days':
                newRange = [dayjs().subtract(30, 'days'), dayjs()];
                break;
            case 'last_7_days':
                newRange = [dayjs().subtract(7, 'days'), dayjs()];
                break;
            case 'mtd':
                newRange = [dayjs().startOf('month'), dayjs()];
                break;
            case 'last_month':
                newRange = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                break;
            default:
                newRange = [dayjs().subtract(30, 'days'), dayjs()];
        }

        setDateRange(newRange);
    };

    const loadStats = () => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');
            api.getUtmSourceStats(startDate, endDate, onlyNewCustomers)
                .then(data => {
                    setUtmStats(asArray(data));
                })
                .catch(err => {
                    console.error('Error loading UTM stats:', err);
                    setUtmStats([]);
                });
            api.getCompanyGrowthStats(startDate, endDate).then(data => setCompanyGrowthData(asArray(data))).catch(err => console.error('Error loading company growth:', err));
            api.getRevenueGrowthStats(startDate, endDate).then(data => setRevenueGrowthData(asArray(data))).catch(err => console.error('Error loading revenue growth:', err));
            api.getSignupsPerDayStats(startDate, endDate).then(data => setSignupsPerDayData(asArray(data))).catch(err => console.error('Error loading signups:', err));
            api.getAnnotations(startDate, endDate).then(data => setAnnotations(asArray(data))).catch(err => console.error('Error loading annotations:', err));
        }
        api.getUserCountByLanguage()
            .then(data => {
                setUserCountByLanguage(asArray(data));
            })
            .catch(err => {
                console.error('Error loading user count by language:', err);
                setUserCountByLanguage([]);
            });
    };

    useEffect(() => {
        loadStats();
    }, []);

    useEffect(() => {
        loadStats();
    }, [dateRange]);

    useEffect(() => {
        loadStats();
    }, [onlyNewCustomers]);

    const handleTabChange = (key) => {
        switch(key) {
            case 'stats':
                navigate('/admin');
                break;
            case 'suppliers':
                navigate('/admin/suppliers');
                break;
            case 'icons':
                navigate('/admin/icons');
                break;
            case 'users':
                navigate('/admin/users');
                break;
            case 'blog':
                navigate('/admin/blog');
                break;
            case 'ad-spends':
                navigate('/admin/ad-spends');
                break;
            case 'help-videos':
                navigate('/admin/help-videos');
                break;
            case 'affiliates':
                navigate('/admin/affiliates');
                break;
            default:
                navigate('/admin');
        }
    };

    function moneyKpi(value) {
        return value.toLocaleString('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    function numberKpi(value) {
        return value.toLocaleString('nl-NL', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }

    const searchUser = () => {
        api.searchUser(searchForm.getFieldValue('email')).then((users) => {
            setUsers(users)
        })
    }

    const openEditPlanModal = (user) => {
        setEditingUser(user);
        editPlanForm.setFieldsValue({
            plan_id: user.plan_id,
            plan_expires_at: user.plan_expires_at ? dayjs(user.plan_expires_at) : null
        });
        setEditPlanModalOpen(true);
    }

    const handleEditPlanSubmit = () => {
        if (!editingUser || !editingUser.company_id) {
            message.error('User has no company');
            return;
        }

        setEditPlanLoading(true);
        const values = editPlanForm.getFieldsValue();
        const data = {
            plan_id: values.plan_id,
            plan_expires_at: values.plan_expires_at ? values.plan_expires_at.toISOString() : null
        };

        api.updateCompanyPlan(editingUser.company_id, data).then((result) => {
            message.success('Company plan updated successfully');
            setEditPlanModalOpen(false);
            setEditingUser(null);
            // Refresh the user list
            searchUser();
        }).catch(() => {
            message.error('Failed to update company plan');
        }).finally(() => {
            setEditPlanLoading(false);
        });
    }

    useEffect(() => {
        api.getAdminPlans().then(setPlans).catch(() => {});
    }, []);

    useEffect(() => {
        api.get_all_blog_posts().then((posts) => {
            setBlogPosts(posts)
        })
    }, []);

    const generateBlogPosts = () => {
        setGenerateLoading(true)
        api.generate_blog_posts_from_search_terms({
            search_terms: generateForm.getFieldValue('search_terms'),
            language: generateForm.getFieldValue('language') || 'nl'
        }).then((result) => {
            message.success(`Queued generation for ${result.count} search terms. Each will generate 10 blog posts.`)
            setGenerateLoading(false)
            setGenerateModalOpen(false)
            generateForm.resetFields()
        }).catch(() => {
            message.error('Failed to queue blog post generation')
            setGenerateLoading(false)
        })
    }

    const chartAnnotations = annotations.map(a => ({
        x: new Date(a.date).getTime(),
        borderColor: a.color,
        strokeDashArray: 4,
        label: {
            text: a.label,
            orientation: 'vertical',
            style: { fontSize: '11px' }
        }
    }));

    const handleCreateAnnotation = () => {
        setAnnotationLoading(true);
        const values = annotationForm.getFieldsValue();
        api.createAnnotation({
            date: values.date.format('YYYY-MM-DD'),
            label: values.label,
            description: values.description || null,
            color: values.color || '#FF4560',
        }).then(() => {
            message.success('Annotation created');
            setAnnotationModalOpen(false);
            annotationForm.resetFields();
            loadStats();
        }).catch(() => {
            message.error('Failed to create annotation');
        }).finally(() => {
            setAnnotationLoading(false);
        });
    };

    const handleDeleteAnnotation = (id) => {
        api.deleteAnnotation(id).then(() => {
            message.success('Annotation deleted');
            loadStats();
        }).catch(() => {
            message.error('Failed to delete annotation');
        });
    };

    const handleEditAnnotation = (record) => {
        setEditingAnnotation(record);
        editAnnotationForm.setFieldsValue({
            date: dayjs(record.date),
            label: record.label,
            description: record.description,
            color: record.color,
        });
        setEditAnnotationModalOpen(true);
    };

    const handleUpdateAnnotation = () => {
        setEditAnnotationLoading(true);
        const values = editAnnotationForm.getFieldsValue();
        api.updateAnnotation(editingAnnotation.id, {
            date: values.date.format('YYYY-MM-DD'),
            label: values.label,
            description: values.description || null,
            color: values.color || '#FF4560',
        }).then(() => {
            message.success('Annotation updated');
            setEditAnnotationModalOpen(false);
            setEditingAnnotation(null);
            editAnnotationForm.resetFields();
            loadStats();
        }).catch(() => {
            message.error('Failed to update annotation');
        }).finally(() => {
            setEditAnnotationLoading(false);
        });
    };

    return (
        <>
            <Modal
                open={editPlanModalOpen}
                onCancel={() => {
                    setEditPlanModalOpen(false);
                    setEditingUser(null);
                }}
                footer={null}
                title={`Edit Company Plan - ${editingUser?.company_name || ''}`}
            >
                <Form form={editPlanForm} layout="vertical">
                    <Form.Item name="plan_id" label="Plan">
                        <Select placeholder="Select a plan">
                            {plans.map(plan => (
                                <Select.Option key={plan.id} value={plan.id}>
                                    {plan.name} (€{plan.price})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="plan_expires_at" label="Plan Expires At">
                        <DatePicker style={{width: '100%'}} />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            onClick={handleEditPlanSubmit}
                            loading={editPlanLoading}
                        >
                            Save Changes
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={generateModalOpen}
                onCancel={() => setGenerateModalOpen(false)}
                footer={null}
                width={"80%"}
                title="Generate Blog Posts from Search Terms"
            >
                <Form form={generateForm} layout={"vertical"} initialValues={{ language: 'nl' }}>
                    <Form.Item name={"language"} label={"Taal / Language"}>
                        <Select>
                            <Select.Option value="nl">Nederlands (NL)</Select.Option>
                            <Select.Option value="de">Deutsch (DE)</Select.Option>
                            <Select.Option value="fr">Français (FR)</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name={"search_terms"}
                        label={"Search Terms (one per line)"}
                        extra="Paste your keyword research results here. Each line will generate 10 blog posts."
                        rules={[{ required: true, message: 'Please enter at least one search term' }]}
                    >
                        <TextArea
                            rows={10}
                            placeholder="moestuin beginnen&#10;groenten kweken&#10;tomaten zaaien&#10;..."
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            onClick={() => generateBlogPosts()}
                            loading={generateLoading}
                        >
                            Generate Blog Posts
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={annotationModalOpen}
                onCancel={() => setAnnotationModalOpen(false)}
                footer={null}
                title="Add Chart Annotation"
            >
                <Form form={annotationForm} layout="vertical" initialValues={{ color: '#FF4560' }}>
                    <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Please select a date' }]}>
                        <DatePicker style={{width: '100%'}} />
                    </Form.Item>
                    <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Please enter a label' }]}>
                        <Input placeholder="e.g. Feature launch, Price change" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={2} placeholder="Optional longer description" />
                    </Form.Item>
                    <Form.Item name="color" label="Color">
                        <Select>
                            <Select.Option value="#FF4560">Red</Select.Option>
                            <Select.Option value="#008FFB">Blue</Select.Option>
                            <Select.Option value="#00E396">Green</Select.Option>
                            <Select.Option value="#FEB019">Orange</Select.Option>
                            <Select.Option value="#775DD0">Purple</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={handleCreateAnnotation} loading={annotationLoading}>
                            Add Annotation
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={editAnnotationModalOpen}
                onCancel={() => {
                    setEditAnnotationModalOpen(false);
                    setEditingAnnotation(null);
                }}
                footer={null}
                title="Edit Chart Annotation"
            >
                <Form form={editAnnotationForm} layout="vertical">
                    <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Please select a date' }]}>
                        <DatePicker style={{width: '100%'}} />
                    </Form.Item>
                    <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Please enter a label' }]}>
                        <Input placeholder="e.g. Feature launch, Price change" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={2} placeholder="Optional longer description" />
                    </Form.Item>
                    <Form.Item name="color" label="Color">
                        <Select>
                            <Select.Option value="#FF4560">Red</Select.Option>
                            <Select.Option value="#008FFB">Blue</Select.Option>
                            <Select.Option value="#00E396">Green</Select.Option>
                            <Select.Option value="#FEB019">Orange</Select.Option>
                            <Select.Option value="#775DD0">Purple</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={handleUpdateAnnotation} loading={editAnnotationLoading}>
                            Save Changes
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <h1>Admin</h1>

            <Row style={{marginBottom: 16}} gutter={16} justify="end">
                <Col>
                    <Select
                        value={dateRangePreset}
                        onChange={handlePresetChange}
                        style={{width: 150}}
                    >
                        <Select.Option value="all_time">All Time</Select.Option>
                        <Select.Option value="last_year">Last Year</Select.Option>
                        <Select.Option value="ytd">YTD</Select.Option>
                        <Select.Option value="last_30_days">Last 30 Days</Select.Option>
                        <Select.Option value="last_7_days">Last 7 Days</Select.Option>
                        <Select.Option value="mtd">MTD</Select.Option>
                        <Select.Option value="last_month">Last Month</Select.Option>
                    </Select>
                </Col>
                <Col>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => {
                            setDateRange(dates);
                            setDateRangePreset(null);
                        }}
                        format="YYYY-MM-DD"
                    />
                </Col>
                <Col>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => setAnnotationModalOpen(true)}
                    >
                        Add Annotation
                    </Button>
                </Col>
            </Row>

            <Tabs
                activeKey={getActiveTab()}
                onChange={handleTabChange}
                items={[
                    {
                        label: 'Stats',
                        key: 'stats',
                        children: (
                            <>
                                <Row style={{marginBottom: 16}} gutter={16}>
                                    <Col>
                                        <Button
                                            danger
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: 'Clear Crawler Queue',
                                                    content: 'Are you sure you want to clear all pending crawler jobs? This cannot be undone.',
                                                    onOk: () => {
                                                        api.clearCrawlerQueue().then(() => {
                                                            message.success('Crawler queue cleared successfully');
                                                        }).catch(() => {
                                                            message.error('Failed to clear crawler queue');
                                                        });
                                                    }
                                                });
                                            }}
                                        >
                                            Clear Crawler Queue
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: 'Start All Supplier Crawls',
                                                    content: 'This will start crawling all suppliers that have crawling enabled. Continue?',
                                                    onOk: () => {
                                                        api.startAllSupplierCrawls().then((result) => {
                                                            message.success(`Started crawling ${result.count} suppliers`);
                                                        }).catch(() => {
                                                            message.error('Failed to start supplier crawls');
                                                        });
                                                    }
                                                });
                                            }}
                                        >
                                            Start All Supplier Crawls
                                        </Button>
                                    </Col>
                                </Row>
                                <Row style={{marginTop: 24, marginBottom: 24}} gutter={[24, 24]}>
                                    <Col xs={24} md={12}>
                                        <h3>Total Companies</h3>
                                        {companyGrowthData.length > 0 && (
                                            <StaticChart
                                                options={{
                                                    chart: {
                                                        id: 'company-growth-chart',
                                                        type: 'area',
                                                        toolbar: {
                                                            show: true
                                                        }
                                                    },
                                                    dataLabels: {
                                                        enabled: false
                                                    },
                                                    stroke: {
                                                        curve: 'smooth',
                                                        width: 2
                                                    },
                                                    xaxis: {
                                                        type: 'datetime',
                                                        categories: companyGrowthData.map(d => d.date),
                                                        labels: {
                                                            format: 'dd MMM'
                                                        }
                                                    },
                                                    yaxis: {
                                                        title: {
                                                            text: 'Total Companies'
                                                        },
                                                        labels: {
                                                            formatter: function(val) {
                                                                return Math.floor(val);
                                                            }
                                                        }
                                                    },
                                                    fill: {
                                                        type: 'gradient',
                                                        gradient: {
                                                            shadeIntensity: 1,
                                                            opacityFrom: 0.7,
                                                            opacityTo: 0.3,
                                                            stops: [0, 90, 100]
                                                        }
                                                    },
                                                    colors: ['#006e2f'],
                                                    annotations: {
                                                        xaxis: chartAnnotations
                                                    },
                                                    tooltip: {
                                                        x: {
                                                            format: 'dd MMM yyyy'
                                                        }
                                                    }
                                                }}
                                                series={[{
                                                    name: 'Total Companies',
                                                    data: companyGrowthData.map(d => d.count)
                                                }]}
                                                type="area"
                                                height={350}
                                            />
                                        )}
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <h3>Total Revenue</h3>
                                        {revenueGrowthData.length > 0 && (
                                            <StaticChart
                                                options={{
                                                    chart: {
                                                        id: 'revenue-growth-chart',
                                                        type: 'area',
                                                        toolbar: {
                                                            show: true
                                                        }
                                                    },
                                                    dataLabels: {
                                                        enabled: false
                                                    },
                                                    stroke: {
                                                        curve: 'smooth',
                                                        width: 2
                                                    },
                                                    xaxis: {
                                                        type: 'datetime',
                                                        categories: revenueGrowthData.map(d => d.date),
                                                        labels: {
                                                            format: 'dd MMM'
                                                        }
                                                    },
                                                    yaxis: {
                                                        title: {
                                                            text: 'Total Revenue'
                                                        },
                                                        labels: {
                                                            formatter: function(val) {
                                                                return moneyKpi(val);
                                                            }
                                                        }
                                                    },
                                                    fill: {
                                                        type: 'gradient',
                                                        gradient: {
                                                            shadeIntensity: 1,
                                                            opacityFrom: 0.7,
                                                            opacityTo: 0.3,
                                                            stops: [0, 90, 100]
                                                        }
                                                    },
                                                    colors: ['#1890ff'],
                                                    annotations: {
                                                        xaxis: chartAnnotations
                                                    },
                                                    tooltip: {
                                                        x: {
                                                            format: 'dd MMM yyyy'
                                                        },
                                                        y: {
                                                            formatter: function(val) {
                                                                return moneyKpi(val);
                                                            }
                                                        }
                                                    }
                                                }}
                                                series={[{
                                                    name: 'Total Revenue',
                                                    data: revenueGrowthData.map(d => d.revenue)
                                                }]}
                                                type="area"
                                                height={350}
                                            />
                                        )}
                                    </Col>
                                </Row>
                                <Row style={{marginTop: 24, marginBottom: 24}} gutter={24}>
                                    <Col span={24}>
                                        <h3>Signups Per Day</h3>
                                        {signupsPerDayData.length > 0 && (
                                            <StaticChart
                                                options={{
                                                    chart: {
                                                        id: 'signups-per-day-chart',
                                                        type: 'bar',
                                                        toolbar: {
                                                            show: true
                                                        }
                                                    },
                                                    dataLabels: {
                                                        enabled: false
                                                    },
                                                    xaxis: {
                                                        type: 'datetime',
                                                        categories: signupsPerDayData.map(d => d.date),
                                                        labels: {
                                                            format: 'dd MMM'
                                                        }
                                                    },
                                                    yaxis: {
                                                        title: {
                                                            text: 'Signups'
                                                        },
                                                        labels: {
                                                            formatter: function(val) {
                                                                return Math.floor(val);
                                                            }
                                                        }
                                                    },
                                                    colors: ['#52c41a'],
                                                    annotations: {
                                                        xaxis: chartAnnotations
                                                    },
                                                    tooltip: {
                                                        x: {
                                                            format: 'dd MMM yyyy'
                                                        }
                                                    }
                                                }}
                                                series={[{
                                                    name: 'Signups',
                                                    data: signupsPerDayData.map(d => d.count)
                                                }]}
                                                type="bar"
                                                height={350}
                                            />
                                        )}
                                    </Col>
                                </Row>
                                {annotations.length > 0 && (
                                    <Row style={{marginTop: 24, marginBottom: 24}}>
                                        <Col span={24}>
                                            <h3>Chart Annotations</h3>
                                            <Table
                                                dataSource={annotations}
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                columns={[
                                                    {
                                                        title: 'Date',
                                                        dataIndex: 'date',
                                                        key: 'date',
                                                    },
                                                    {
                                                        title: 'Label',
                                                        dataIndex: 'label',
                                                        key: 'label',
                                                    },
                                                    {
                                                        title: 'Description',
                                                        dataIndex: 'description',
                                                        key: 'description',
                                                        responsive: ['md'],
                                                        render: (value) => value || '-'
                                                    },
                                                    {
                                                        title: 'Color',
                                                        dataIndex: 'color',
                                                        key: 'color',
                                                        responsive: ['md'],
                                                        render: (value) => (
                                                            <span style={{display: 'inline-block', width: 16, height: 16, backgroundColor: value, borderRadius: 2}} />
                                                        )
                                                    },
                                                    {
                                                        title: '',
                                                        key: 'actions',
                                                        render: (_, record) => (
                                                            <>
                                                                <Button
                                                                    type="text"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => handleEditAnnotation(record)}
                                                                />
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() => {
                                                                        Modal.confirm({
                                                                            title: 'Delete Annotation',
                                                                            content: `Delete annotation "${record.label}"?`,
                                                                            onOk: () => handleDeleteAnnotation(record.id)
                                                                        });
                                                                    }}
                                                                />
                                                            </>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </Col>
                                    </Row>
                                )}
                                <hr/>
                                <Row style={{marginTop: 24, marginBottom: 24}}>
                                    <Col span={24}>
                                        <h3>UTM Source Statistics</h3>
                                        <Checkbox
                                            checked={onlyNewCustomers}
                                            onChange={(e) => setOnlyNewCustomers(e.target.checked)}
                                            style={{marginBottom: 16}}
                                        >
                                            Only new customers (exclude renewals, only show customers who signed up in date range)
                                        </Checkbox>
                                        {utmStats.length === 0 && <p>No UTM source data available for the selected date range.</p>}
                                        {utmStats.length > 0 && (() => {
                                            // Calculate totals
                                            const totals = utmStats.reduce((acc, row) => ({
                                                count: acc.count + (row.count || 0),
                                                payment_count: acc.payment_count + (row.payment_count || 0),
                                                paying_companies: acc.paying_companies + (row.paying_companies || 0),
                                                revenue: acc.revenue + (row.revenue || 0),
                                                ad_spend: acc.ad_spend + (row.ad_spend || 0)
                                            }), { count: 0, payment_count: 0, paying_companies: 0, revenue: 0, ad_spend: 0 });

                                            // Calculate total metrics
                                            const totalConversion = totals.count > 0 ? (totals.paying_companies / totals.count) * 100 : null;
                                            const totalUac = totals.count > 0 && totals.ad_spend > 0 ? totals.ad_spend / totals.count : null;
                                            const totalCac = totals.paying_companies > 0 && totals.ad_spend > 0 ? totals.ad_spend / totals.paying_companies : null;
                                            const totalRoi = totals.ad_spend > 0 ? ((totals.revenue - totals.ad_spend) / totals.ad_spend) * 100 : null;
                                            const totalCltv = totals.paying_companies > 0 ? totals.revenue / totals.paying_companies : null;

                                            const totalRow = {
                                                utm_medium: 'TOTAL',
                                                count: totals.count,
                                                payment_count: totals.payment_count,
                                                paying_companies: totals.paying_companies,
                                                conversion_rate: totalConversion,
                                                revenue: totals.revenue,
                                                ad_spend: totals.ad_spend,
                                                uac: totalUac,
                                                cac: totalCac,
                                                roi: totalRoi,
                                                cltv: totalCltv,
                                                isTotal: true
                                            };

                                            return (
                                            <Table
                                                dataSource={[...utmStats, totalRow]}
                                                rowKey={(record) => record.utm_medium || 'not-set'}
                                                rowClassName={(record) => record.isTotal ? 'total-row' : ''}
                                                scroll={{x: 'max-content'}}
                                                columns={[
                                                    {
                                                        title: 'UTM Medium',
                                                        dataIndex: 'utm_medium',
                                                        key: 'utm_medium',
                                                        fixed: 'left',
                                                        render: (value, record) => record.isTotal ? <strong>{value}</strong> : (value || '(not set)')
                                                    },
                                                    {
                                                        title: 'Signups',
                                                        dataIndex: 'count',
                                                        key: 'count',
                                                        render: (value, record) => record.isTotal ? <strong>{numberKpi(value)}</strong> : numberKpi(value)
                                                    },
                                                    {
                                                        title: 'Conv.',
                                                        dataIndex: 'conversion_rate',
                                                        key: 'conversion_rate',
                                                        render: (value, record) => {
                                                            const formatted = value !== null && value !== undefined ? `${value.toFixed(1)}%` : '-';
                                                            return record.isTotal ? <strong>{formatted}</strong> : formatted;
                                                        }
                                                    },
                                                    {
                                                        title: 'Payments',
                                                        dataIndex: 'payment_count',
                                                        key: 'payment_count',
                                                        render: (value, record) => record.isTotal ? <strong>{numberKpi(value)}</strong> : numberKpi(value)
                                                    },
                                                    {
                                                        title: 'Revenue',
                                                        dataIndex: 'revenue',
                                                        key: 'revenue',
                                                        render: (value, record) => record.isTotal ? <strong>{moneyKpi(value)}</strong> : moneyKpi(value)
                                                    },
                                                    {
                                                        title: 'Ad Spend',
                                                        dataIndex: 'ad_spend',
                                                        key: 'ad_spend',
                                                        render: (value, record) => record.isTotal ? <strong>{moneyKpi(value)}</strong> : moneyKpi(value)
                                                    },
                                                    {
                                                        title: 'Profit',
                                                        dataIndex: 'revenue',
                                                        key: 'profit',
                                                        render: (value, record) => {
                                                            const profit = moneyKpi(value - record.ad_spend);
                                                            return record.isTotal ? <strong>{profit}</strong> : profit;
                                                        }
                                                    },
                                                    {
                                                        title: 'ROI',
                                                        dataIndex: 'roi',
                                                        key: 'roi',
                                                        render: (value, record) => {
                                                            const formatted = value !== null && value !== undefined ? `${value.toFixed(2)}%` : '-';
                                                            return record.isTotal ? <strong>{formatted}</strong> : formatted;
                                                        }
                                                    },
                                                    {
                                                        title: 'UAC',
                                                        dataIndex: 'uac',
                                                        key: 'uac',
                                                        render: (value, record) => {
                                                            const formatted = value !== null && value !== undefined ? moneyKpi(value) : '-';
                                                            return record.isTotal ? <strong>{formatted}</strong> : formatted;
                                                        }
                                                    },
                                                    {
                                                        title: 'CAC',
                                                        dataIndex: 'cac',
                                                        key: 'cac',
                                                        render: (value, record) => {
                                                            const formatted = value !== null && value !== undefined ? moneyKpi(value) : '-';
                                                            return record.isTotal ? <strong>{formatted}</strong> : formatted;
                                                        }
                                                    },
                                                    {
                                                        title: 'CLTV',
                                                        dataIndex: 'cltv',
                                                        key: 'cltv',
                                                        render: (value, record) => {
                                                            const formatted = value !== null && value !== undefined ? moneyKpi(value) : '-';
                                                            return record.isTotal ? <strong>{formatted}</strong> : formatted;
                                                        }
                                                    },
                                                ]}
                                                pagination={false}
                                            />
                                            );
                                        })()}
                                    </Col>
                                </Row>
                                <hr/>
                                <Row style={{marginTop: 24, marginBottom: 24}}>
                                    <Col span={24}>
                                        <h3>User Count by Language</h3>
                                        {userCountByLanguage.length === 0 && <p>No user data available.</p>}
                                        {userCountByLanguage.length > 0 && (
                                            <Table
                                                dataSource={userCountByLanguage}
                                                rowKey={(record) => record.language || 'unknown'}
                                                columns={[
                                                    {
                                                        title: 'Language',
                                                        dataIndex: 'language',
                                                        key: 'language',
                                                        render: (value) => value || 'unknown'
                                                    },
                                                    {
                                                        title: 'User Count',
                                                        dataIndex: 'count',
                                                        key: 'count',
                                                        render: (value) => numberKpi(value)
                                                    }
                                                ]}
                                                pagination={false}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </>
                        )
                    },
                    {
                        label: 'Suppliers',
                        key: 'suppliers',
                        children: null
                    },
                    {
                        label: 'Icons',
                        key: 'icons',
                        children: null
                    },
                    {
                        label: 'Users',
                        key: 'users',
                        children: (
                            <Row>
                                <Col span={24}>
                                    <h2>Users</h2>
                                    <Form form={searchForm}>
                                        <Form.Item label={"Search by email"} name={"email"}>
                                            <Input onPressEnter={searchUser}/>
                                        </Form.Item>
                                    </Form>
                                </Col>
                                <Col span={24}>
                                    {users.length > 0 && (
                                        <Table style={{width: '100%'}} dataSource={users} rowKey="id" columns={[
                                            {title: 'ID', dataIndex: 'id', key: 'id'},
                                            {title: 'Name', dataIndex: 'name', key: 'name'},
                                            {title: 'Email', dataIndex: 'email', key: 'email'},
                                            {title: 'Company', dataIndex: 'company_name', key: 'company_name'},
                                            {title: 'Plan', dataIndex: 'plan_name', key: 'plan_name', render: (value) => value || '-'},
                                            {
                                                title: 'Plan Expires',
                                                dataIndex: 'plan_expires_at',
                                                key: 'plan_expires_at',
                                                render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-'
                                            },
                                            {
                                                title: 'Actions',
                                                key: 'actions',
                                                render: (user) => (
                                                    <>
                                                        {user.company_id && (
                                                            <Button
                                                                type="link"
                                                                icon={<EditOutlined />}
                                                                onClick={() => openEditPlanModal(user)}
                                                                style={{marginRight: 8}}
                                                                title="Edit company plan"
                                                            />
                                                        )}
                                                        <Link to={`/admin/impersonate/${user.id}`}>Impersonate</Link>
                                                        <Button
                                                            type="link"
                                                            icon={<MailOutlined />}
                                                            onClick={() => {
                                                                Modal.confirm({
                                                                    title: 'Send Password Reset',
                                                                    content: `Send a password reset email to ${user.email}?`,
                                                                    onOk: () => {
                                                                        api.adminSendPasswordReset(user.id).then(() => {
                                                                            message.success('Password reset email sent');
                                                                        }).catch(() => {
                                                                            message.error('Failed to send password reset email');
                                                                        });
                                                                    }
                                                                });
                                                            }}
                                                            title="Send password reset email"
                                                        >
                                                            Reset Password
                                                        </Button>
                                                    </>
                                                )
                                            },
                                        ]} pagination={false}></Table>
                                    )}
                                </Col>
                            </Row>
                        )
                    },
                    {
                        label: 'Blog Posts',
                        key: 'blog',
                        children: (
                            <Row>
                                <Col span={24}>
                                    <h2>Blog posts</h2>
                                    <Button onClick={() => setGenerateModalOpen(true)} type={"primary"} style={{marginBottom: 16}}>
                                        Generate from Search Terms
                                    </Button>

                                    <Table pagination={false} dataSource={blogPosts} columns={[
                                        {
                                            title: 'Generated',
                                            dataIndex: 'is_generated',
                                            key: 'is_generated',
                                            render: (value) => value ? 'Yes' : 'No'
                                        },
                                        {title: 'Lang', dataIndex: 'language', key: 'language'},
                                        {title: 'Prompt', dataIndex: 'prompt', key: 'prompt'},
                                        {title: 'Meta Keywords', dataIndex: 'meta_keywords', key: 'meta_keywords'},
                                        {
                                            title: 'View',
                                            dataIndex: 'slug',
                                            key: 'slug',
                                            render: (id) => <Link target={"_blank"} to={api.get_blog_url(id)}>View</Link>
                                        },
                                    ]}/>
                                </Col>
                            </Row>
                        )
                    },
                    {
                        label: 'Ad Spends',
                        key: 'ad-spends',
                        children: null
                    },
                    {
                        label: 'Help Videos',
                        key: 'help-videos',
                        children: null
                    },
                    {
                        label: 'Affiliates',
                        key: 'affiliates',
                        children: null
                    },
                ]}
            />
        </>
    )
}
